import React, { useEffect, useMemo, useState } from "react";
import { Plus, ClipboardList, Trash2, Edit, Clock, Stethoscope, Users, Wrench, Search, X, Activity } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const ROLE_META = {
  physician: { label: "Physician", icon: Stethoscope, color: "#0B7E8C" },
  nurse: { label: "Nurse", icon: Users, color: "#8B5CF6" },
  technician: { label: "Technician", icon: Wrench, color: "#F59E0B" },
  physiotherapist: { label: "Physiotherapist", icon: Activity, color: "#0EA5E9" },
};
const PRIORITIES = ["routine", "urgent", "critical"];
const EMPTY_ROLE = { role: "nurse", medical_specialty: "", sub_specialty: "", specialty_type: "", skills: "", count: 1 };
const EMPTY = { code: "", name: "", category: "", description: "", est_duration_min: 30, default_priority: "routine", active: true, equipment: "", required_roles: [{ ...EMPTY_ROLE }] };

const toForm = (s) => ({
  code: s.code || "", name: s.name || "", category: s.category || "", description: s.description || "",
  est_duration_min: s.est_duration_min ?? 30, default_priority: s.default_priority || "routine",
  active: s.active !== false, equipment: (s.equipment || []).join(", "),
  required_roles: (s.required_roles || []).map((r) => ({
    role: r.role || "nurse", medical_specialty: r.medical_specialty || "", sub_specialty: r.sub_specialty || "",
    specialty_type: r.specialty_type || "", skills: (r.skills || []).join(", "), count: r.count || 1,
  })) || [{ ...EMPTY_ROLE }],
});

export default function ServiceCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sheet, setSheet] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState({ open: false });

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/service-catalog");
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => `${s.name} ${s.code} ${s.category || ""}`.toLowerCase().includes(q));
  }, [items, search]);

  function openNew() { setEditing(null); setForm({ ...EMPTY, required_roles: [{ ...EMPTY_ROLE }] }); setSheet(true); }
  function openEdit(s) { setEditing(s); setForm(toForm(s)); setSheet(true); }

  function setRole(idx, key, val) {
    setForm((f) => ({ ...f, required_roles: f.required_roles.map((r, i) => (i === idx ? { ...r, [key]: val } : r)) }));
  }
  function addRole() { setForm((f) => ({ ...f, required_roles: [...f.required_roles, { ...EMPTY_ROLE }] })); }
  function removeRole(idx) { setForm((f) => ({ ...f, required_roles: f.required_roles.filter((_, i) => i !== idx) })); }

  async function save() {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and name are required"); return; }
    if (form.required_roles.length === 0) { toast.error("Add at least one required role"); return; }
    setSaving(true);
    const payload = {
      code: form.code.trim(), name: form.name.trim(), category: form.category.trim() || null,
      description: form.description.trim() || null, est_duration_min: Number(form.est_duration_min) || 30,
      default_priority: form.default_priority, active: form.active,
      equipment: form.equipment.split(",").map((x) => x.trim()).filter(Boolean),
      required_roles: form.required_roles.map((r) => ({
        role: r.role, medical_specialty: r.medical_specialty.trim() || null, sub_specialty: r.sub_specialty.trim() || null,
        specialty_type: r.specialty_type.trim() || null, count: Number(r.count) || 1,
        skills: r.skills.split(",").map((x) => x.trim()).filter(Boolean),
      })),
    };
    try {
      if (editing) await api.put(`/service-catalog/${editing.id}`, payload);
      else await api.post("/service-catalog", payload);
      toast.success(editing ? "Service updated" : "Service created");
      setSheet(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function doDelete(s) {
    try { await api.delete(`/service-catalog/${s.id}`); toast.success("Service deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setConfirm({ open: false }); }
  }

  return (
    <div data-testid="service-catalog-page">
      <PageHeader
        title="Service Catalog"
        subtitle="Define each home-care service and the provider roles, specialty, skills, duration & equipment it needs. Used by the Dispatch Orchestrator."
        actions={
          <Button onClick={openNew} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="service-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Service
          </Button>
        }
      />

      <div className="hah-card p-3 mb-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-[#94A3B8]" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search services…"
               data-testid="service-search" className="flex-1 bg-transparent text-sm outline-none" />
        <span className="text-xs text-[#94A3B8]">{filtered.length} service(s)</span>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-10 text-center text-sm text-[#6B7280]" data-testid="service-empty">No services found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((s) => (
            <div key={s.id} data-testid={`service-card-${s.code}`}
                 className="hah-card p-4 flex flex-col gap-2 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-[#1F2937] truncate">{s.name}</div>
                  <div className="text-[11px] text-[#94A3B8] font-mono">{s.code}{s.category ? ` · ${s.category}` : ""}</div>
                </div>
                {!s.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B] font-medium shrink-0">Inactive</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(s.required_roles || []).map((r, i) => {
                  const m = ROLE_META[r.role] || ROLE_META.nurse;
                  const Icon = m.icon;
                  const tags = [r.medical_specialty, r.sub_specialty, ...(r.skills || [])].filter(Boolean).join(" · ");
                  return (
                    <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium"
                          style={{ background: `${m.color}14`, color: m.color }} title={tags}>
                      <Icon className="w-3 h-3" /> {r.count > 1 ? `${r.count}× ` : ""}{m.label}
                    </span>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 text-[12px] text-[#6B7280] mt-auto pt-1">
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.est_duration_min} min</span>
                <span className="capitalize">{s.default_priority}</span>
                {(s.equipment || []).length > 0 && <span className="inline-flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> {s.equipment.length}</span>}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-[#F1F5F9]">
                <button onClick={() => openEdit(s)} data-testid={`service-edit-${s.code}`}
                        className="text-[12px] text-[#0B7E8C] hover:underline inline-flex items-center gap-1">
                  <Edit className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => setConfirm({ open: true, item: s })} data-testid={`service-delete-${s.code}`}
                        className="text-[12px] text-[#EF4444] hover:underline inline-flex items-center gap-1 ml-auto">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="service-sheet">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit Service" : "New Service"}</SheetTitle>
            <SheetDescription>Map this service to the provider role(s), skills, duration and equipment it requires.</SheetDescription>
          </SheetHeader>

          <div className="space-y-4 mt-5">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code *"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="WOUND_CARE" data-testid="service-form-code" /></Field>
              <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Nursing" data-testid="service-form-category" /></Field>
            </div>
            <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Wound Care" data-testid="service-form-name" /></Field>
            <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" data-testid="service-form-desc" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Duration (min)"><Input type="number" value={form.est_duration_min} onChange={(e) => setForm({ ...form, est_duration_min: e.target.value })} data-testid="service-form-duration" /></Field>
              <Field label="Priority">
                <Select value={form.default_priority} onValueChange={(v) => setForm({ ...form, default_priority: v })}>
                  <SelectTrigger data-testid="service-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Active">
                <Select value={form.active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, active: v === "yes" })}>
                  <SelectTrigger data-testid="service-form-active"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="yes">Active</SelectItem><SelectItem value="no">Inactive</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Equipment (comma-separated)"><Input value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} placeholder="dressing_kit, gloves" data-testid="service-form-equipment" /></Field>

            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[13px] font-semibold text-[#1F2937]">Required Roles</div>
                <button onClick={addRole} className="text-[12px] text-[#0B7E8C] hover:underline inline-flex items-center gap-1" data-testid="service-add-role">
                  <Plus className="w-3.5 h-3.5" /> Add role
                </button>
              </div>
              <div className="space-y-3">
                {form.required_roles.map((r, idx) => (
                  <div key={idx} className="rounded-lg border border-[#E5E7EB] p-3 space-y-2" data-testid={`service-role-row-${idx}`}>
                    <div className="flex items-center gap-2">
                      <Select value={r.role} onValueChange={(v) => setRole(idx, "role", v)}>
                        <SelectTrigger className="h-8 flex-1" data-testid={`service-role-select-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.keys(ROLE_META).map((k) => <SelectItem key={k} value={k}>{ROLE_META[k].label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" min={1} value={r.count} onChange={(e) => setRole(idx, "count", e.target.value)}
                             className="h-8 w-16" title="Count" data-testid={`service-role-count-${idx}`} />
                      {form.required_roles.length > 1 && (
                        <button onClick={() => removeRole(idx)} className="text-[#EF4444] p-1" data-testid={`service-role-remove-${idx}`}><X className="w-4 h-4" /></button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={r.medical_specialty} onChange={(e) => setRole(idx, "medical_specialty", e.target.value)} placeholder="Medical specialty" className="h-8 text-[13px]" />
                      <Input value={r.sub_specialty} onChange={(e) => setRole(idx, "sub_specialty", e.target.value)} placeholder="Sub-specialty" className="h-8 text-[13px]" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={r.specialty_type} onChange={(e) => setRole(idx, "specialty_type", e.target.value)} placeholder="Specialty level (e.g. Nursing)" className="h-8 text-[13px]" />
                      <Input value={r.skills} onChange={(e) => setRole(idx, "skills", e.target.value)} placeholder="Skills (comma-sep)" className="h-8 text-[13px]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-6 pt-4 border-t border-[#E5E7EB]">
            <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="service-save-btn">
              {saving ? "Saving…" : editing ? "Save changes" : "Create service"}
            </Button>
            <Button variant="outline" onClick={() => setSheet(false)} className="border-[#E5E7EB]">Cancel</Button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title="Delete service?"
        description={`"${confirm.item?.name}" will be permanently removed from the catalog.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => doDelete(confirm.item)}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-[#374151] mb-1">{label}</label>
      {children}
    </div>
  );
}
